import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Pause, Play, FlaskConical, RefreshCw, ShieldOff, Ban, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarWeekView } from "./CalendarWeekView";
import { RevenuePanel } from "./RevenuePanel";

const SUBTABS = [["jobs", "Jobs"], ["leads", "Leads"], ["calendar", "Calendar"], ["analytics", "Analytics"], ["revenue", "Revenue"], ["adapters", "Channels"]];

const STATUS_STYLE = {
  SUCCEEDED: "bg-emerald-100 text-emerald-800", SIMULATED: "bg-amber-100 text-amber-800",
  DRY_RUN: "bg-sky-100 text-sky-800", SKIPPED: "bg-stone-200 text-stone-700", DEAD: "bg-rose-100 text-rose-800",
  CANCELLED: "bg-stone-200 text-stone-500", QUEUED: "bg-indigo-100 text-indigo-800",
  RETRY_SCHEDULED: "bg-orange-100 text-orange-800", RUNNING: "bg-violet-100 text-violet-800",
  SIMULATED_PUBLISHED: "bg-amber-100 text-amber-800", SCHEDULED: "bg-indigo-100 text-indigo-800",
};
const LABEL = { SIMULATED: "Simulated · not sent", SIMULATED_PUBLISHED: "Simulated publish · not posted", DRY_RUN: "Dry run · no effects" };

const err = (e) => toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Request failed");

function Badge({ s, testid }) {
  return <span data-testid={testid} className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLE[s] || "bg-stone-100 text-stone-600"}`}>{LABEL[s] || s}</span>;
}

function Btn({ onClick, children, testid, tone = "stone" }) {
  const tones = { stone: "bg-stone-900 text-white", ghost: "bg-stone-100 text-stone-700", red: "bg-rose-600 text-white" };
  return <button data-testid={testid} onClick={onClick} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-transform active:scale-95 ${tones[tone]}`}>{children}</button>;
}

function Controls({ bizId, onRan }) {
  const [c, setC] = useState(null);
  const load = useCallback(() => api.get(`/aina/automation/controls/${bizId}`).then((r) => setC(r.data)).catch(() => {}), [bizId]);
  useEffect(() => { load(); }, [load]);
  const set = async (patch) => { try { setC((await api.post(`/aina/automation/controls/${bizId}`, patch)).data); } catch (e) { err(e); } };
  const run = async () => { try { const r = await api.post("/aina/automation/worker/run", { business_id: bizId }); toast.success(`Processed ${r.data.processed} due job(s)`); onRan(); } catch (e) { err(e); } };
  if (!c) return null;
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid="automation-controls">
      <Btn testid="automation-pause-toggle" tone={c.paused ? "red" : "ghost"} onClick={() => set({ paused: !c.paused })}>
        {c.paused ? <><Play className="w-3 h-3 inline mr-1" />Resume</> : <><Pause className="w-3 h-3 inline mr-1" />Pause all</>}
      </Btn>
      <Btn testid="automation-dryrun-toggle" tone={c.dry_run ? "stone" : "ghost"} onClick={() => set({ dry_run: !c.dry_run })}>
        <FlaskConical className="w-3 h-3 inline mr-1" />Dry-run {c.dry_run ? "ON" : "OFF"}
      </Btn>
      <Btn testid="automation-run-now" onClick={run}><RefreshCw className="w-3 h-3 inline mr-1" />Run due jobs</Btn>
      {c.paused && <span data-testid="automation-paused-badge" className="text-xs font-bold text-rose-600">PAUSED — no jobs execute</span>}
    </div>
  );
}

function JobsTab({ bizId, tick }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get(`/aina/automation/jobs?business_id=${bizId}`).then((r) => setItems(r.data.items)).catch(err), [bizId]);
  useEffect(() => { load(); }, [load, tick]);
  const act = async (id, a) => { try { await api.post(`/aina/automation/jobs/${id}/${a}`); load(); } catch (e) { err(e); } };
  if (!items) return <Loader2 className="w-4 h-4 animate-spin" />;
  if (!items.length) return <p className="text-sm text-stone-500" data-testid="jobs-empty">No automation jobs yet.</p>;
  return (
    <ul className="divide-y divide-stone-100" data-testid="jobs-list">
      {items.map((j) => (
        <li key={j.job_id} className="py-2 flex items-center gap-3 text-sm" data-testid={`job-row-${j.job_id}`}>
          <Badge s={j.status} testid={`job-status-${j.job_id}`} />
          <span className="font-mono text-xs">{j.workflow}</span>
          <span className="text-xs text-stone-500">try {j.attempts}/{j.max_attempts} · due {new Date(j.run_at).toLocaleString()}</span>
          {j.last_error && <span className="text-xs text-rose-600 truncate max-w-[220px]">{j.last_error}</span>}
          <span className="ml-auto flex gap-1">
            {["QUEUED", "RETRY_SCHEDULED"].includes(j.status) && <button data-testid={`job-cancel-${j.job_id}`} onClick={() => act(j.job_id, "cancel")}><Ban className="w-4 h-4 text-stone-500" /></button>}
            {["DEAD", "CANCELLED"].includes(j.status) && <button data-testid={`job-retry-${j.job_id}`} onClick={() => act(j.job_id, "retry")}><RotateCcw className="w-4 h-4 text-stone-500" /></button>}
          </span>
        </li>
      ))}
    </ul>
  );
}

function LeadForm({ bizId, onDone }) {
  const [f, setF] = useState({ name: "", mobile: "", interest: "", consent: false });
  const submit = async () => {
    try {
      await api.post("/aina/automation/leads", { business_id: bizId, name: f.name, mobile: f.mobile, interest: f.interest, consent: { granted: f.consent, source: "manual_entry" } });
      setF({ name: "", mobile: "", interest: "", consent: false }); onDone();
    } catch (e) { err(e); }
  };
  const inp = "px-3 py-1.5 rounded-lg border border-stone-200 text-sm";
  return (
    <div className="flex flex-wrap gap-2 items-center mb-3" data-testid="lead-form">
      <input data-testid="lead-name-input" className={inp} placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <input data-testid="lead-mobile-input" className={inp} placeholder="Mobile" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} />
      <input data-testid="lead-interest-input" className={`${inp} flex-1 min-w-[180px]`} placeholder="Enquiry" value={f.interest} onChange={(e) => setF({ ...f, interest: e.target.value })} />
      <label className="text-xs flex items-center gap-1"><input data-testid="lead-consent-checkbox" type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} />Consent to contact</label>
      <Btn testid="lead-create-btn" onClick={submit}>Add lead</Btn>
    </div>
  );
}

function LeadRow({ l, reload }) {
  const call = async (path, body = {}) => { try { await api.post(`/aina/automation/leads/${l.crm_lead_id}/${path}`, body); reload(); } catch (e) { err(e); } };
  const id = l.crm_lead_id;
  return (
    <li className="py-2 text-sm flex flex-wrap items-center gap-2" data-testid={`lead-row-${id}`}>
      <span className="font-semibold">{l.name}</span>
      <Badge s={l.stage} testid={`lead-stage-${id}`} />
      <span className="text-xs text-stone-500" data-testid={`lead-score-${id}`}>score {l.score}</span>
      <span className="text-xs" data-testid={`lead-consent-${id}`}>{l.opted_out ? "OPTED OUT" : `consent: ${l.consent?.status}`}</span>
      <span className="text-xs text-stone-500">follow-ups {l.followups_sent} (simulated)</span>
      <span className="ml-auto flex flex-wrap gap-1">
        {!l.opted_out && l.consent?.status !== "GRANTED" && <Btn tone="ghost" testid={`lead-grant-${id}`} onClick={() => call("consent", { granted: true, source: "owner_recorded" })}>Record consent</Btn>}
        <Btn tone="ghost" testid={`lead-followup-${id}`} onClick={() => call("followups", { offsets_h: [24, 72], channel: "whatsapp", message: `Hi ${l.name}, following up on your enquiry.` })}>Schedule 2 follow-ups</Btn>
        <Btn tone="ghost" testid={`lead-reply-${id}`} onClick={() => call("reply", { text: "customer replied" })}>Mark replied</Btn>
        <Btn tone="ghost" testid={`lead-handover-${id}`} onClick={() => call("handover", {})}>Hand over</Btn>
        {!l.opted_out && <Btn tone="red" testid={`lead-optout-${id}`} onClick={() => call("opt-out", { reason: "customer request" })}>Opt-out</Btn>}
      </span>
    </li>
  );
}

function LeadsTab({ bizId, tick }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get(`/aina/automation/leads?business_id=${bizId}`).then((r) => setItems(r.data.items)).catch(err), [bizId]);
  useEffect(() => { load(); }, [load, tick]);
  return (
    <div>
      <LeadForm bizId={bizId} onDone={load} />
      {!items ? <Loader2 className="w-4 h-4 animate-spin" /> : !items.length ? <p className="text-sm text-stone-500" data-testid="leads-empty">No CRM leads yet.</p> :
        <ul className="divide-y divide-stone-100" data-testid="leads-list">{items.map((l) => <LeadRow key={l.crm_lead_id} l={l} reload={load} />)}</ul>}
    </div>
  );
}

function CalendarTab({ bizId, tick }) {
  const [mode, setMode] = useState("week");
  return (
    <div>
      <div className="flex gap-1 mb-3">
        {[["week", "Week"], ["list", "List"]].map(([k, l]) => (
          <button key={k} data-testid={`calendar-mode-${k}`} onClick={() => setMode(k)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${mode === k ? "bg-indigo-600 text-white" : "bg-stone-100 text-stone-600"}`}>{l}</button>
        ))}
      </div>
      {mode === "week" ? <CalendarWeekView bizId={bizId} tick={tick} /> : <CalendarList bizId={bizId} tick={tick} />}
    </div>
  );
}

export function AddToCalendarButton({ bizId, draft }) {
  const [busy, setBusy] = useState(false);
  const add = async () => {
    const t = new Date(); t.setDate(t.getDate() + 1); t.setHours(10, 0, 0, 0);
    setBusy(true);
    try {
      const r = await api.post("/aina/automation/calendar/from-draft", { business_id: bizId, draft_id: draft.draft_id, scheduled_at: t.toISOString() });
      const st = r.data.status === "APPROVED" ? "approved — tap Schedule in Week view" : "draft — needs approval";
      toast.success(r.data.duplicate ? "Already on the calendar" : `Added to calendar (${st})`);
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <button onClick={add} disabled={busy} data-testid={`aina-add-to-calendar-${draft.draft_id}`}
            className="text-[10px] font-black px-2 py-1 rounded-full bg-indigo-600 text-white uppercase disabled:opacity-50">
      {busy ? "Adding…" : "Add to calendar"}
    </button>
  );
}

function CalendarList({ bizId, tick }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get(`/aina/automation/calendar?business_id=${bizId}`).then((r) => setItems(r.data.items)).catch(err), [bizId]);
  useEffect(() => { load(); }, [load, tick]);
  const plan = async () => { try { await api.post("/aina/automation/calendar/plan", { business_id: bizId, days: 7, platforms: ["find2near", "instagram"] }); load(); } catch (e) { err(e); } };
  const act = async (id, a) => { try { await api.post(`/aina/automation/calendar/${id}/${a}`, {}); load(); } catch (e) { err(e); } };
  const NEXT = { DRAFT: ["submit"], REJECTED: ["submit"], PENDING_APPROVAL: ["approve", "reject"], SCHEDULED: ["cancel"], APPROVED: ["schedule", "cancel"] };
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <Btn testid="calendar-plan-btn" onClick={plan}>Generate 7-day plan</Btn>
        <span className="text-xs text-stone-500">Template-based from your catalogue — no AI inference, no invented prices.</span>
      </div>
      {!items ? <Loader2 className="w-4 h-4 animate-spin" /> : !items.length ? <p className="text-sm text-stone-500" data-testid="calendar-empty">No scheduled content.</p> :
        <ul className="divide-y divide-stone-100" data-testid="calendar-list">
          {items.map((i) => (
            <li key={i.item_id} className="py-2 text-sm flex flex-wrap items-center gap-2" data-testid={`cal-row-${i.item_id}`}>
              <span className="text-xs text-stone-500 w-36">{new Date(i.scheduled_at).toLocaleString()}</span>
              <span className="text-xs font-mono">{i.platform}</span>
              <span className="font-medium truncate max-w-[260px]">{i.title}</span>
              <Badge s={i.status} testid={`cal-status-${i.item_id}`} />
              <span className="ml-auto flex gap-1">{(NEXT[i.status] || []).map((a) => <Btn key={a} tone={a === "reject" || a === "cancel" ? "ghost" : "stone"} testid={`cal-${a}-${i.item_id}`} onClick={() => act(i.item_id, a)}>{a}</Btn>)}</span>
            </li>
          ))}
        </ul>}
    </div>
  );
}

function Stat({ k, v, testid }) {
  return <div className="rounded-xl bg-stone-50 p-3" data-testid={testid}><div className="text-[10px] uppercase tracking-wider text-stone-500">{k}</div><div className="text-lg font-bold">{v ?? "—"}</div></div>;
}

function AnalyticsTab({ bizId, tick }) {
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/aina/automation/analytics?business_id=${bizId}`).then((r) => setA(r.data)).catch(err); }, [bizId, tick]);
  if (!a) return <Loader2 className="w-4 h-4 animate-spin" />;
  const block = (title, obj, tone, tid) => (
    <div className="mb-4" data-testid={tid}>
      <h4 className={`text-xs font-bold uppercase mb-2 ${tone}`}>{title}</h4>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{Object.entries(obj).filter(([, v]) => typeof v !== "object" || v === null).map(([k, v]) => <Stat key={k} k={k.replace(/_/g, " ")} v={v} testid={`${tid}-${k}`} />)}</div>
    </div>
  );
  return (
    <div>
      {block("Verified (real external outcomes)", a.verified, "text-emerald-700", "analytics-verified")}
      {block("Recorded in Find2near", a.recorded, "text-stone-700", "analytics-recorded")}
      {block("Simulated (never sent / posted)", a.simulated, "text-amber-700", "analytics-simulated")}
      <p className="text-xs text-stone-600" data-testid="analytics-cpl">Cost per lead: {a.cost_per_lead ?? `unavailable — ${a.unavailable_reasons.cost_per_lead}`}</p>
      <p className="text-xs text-stone-600" data-testid="analytics-roi">ROI: {a.roi ?? `unavailable — ${a.unavailable_reasons.roi}`}</p>
    </div>
  );
}

function AdaptersTab() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/aina/automation/adapters").then((r) => setItems(r.data.adapters)).catch(err); }, []);
  if (!items) return <Loader2 className="w-4 h-4 animate-spin" />;
  return (
    <ul className="grid sm:grid-cols-2 gap-2" data-testid="adapters-list">
      {items.map((a) => (
        <li key={a.id} className="rounded-xl border border-stone-200 p-3" data-testid={`adapter-${a.id}`}>
          <div className="flex items-center gap-2"><ShieldOff className="w-4 h-4 text-rose-500" /><span className="font-semibold text-sm">{a.id}</span>
            <span className="ml-auto text-[10px] font-bold text-rose-600" data-testid={`adapter-status-${a.id}`}>DISABLED · {a.mode}</span></div>
          <p className="text-xs text-stone-500 mt-1">{a.provider}</p>
          <p className="text-[11px] text-stone-600 mt-1">Requires: {a.requires.join(" · ")}</p>
        </li>
      ))}
    </ul>
  );
}

export function AutomationSection({ bizId }) {
  const [sub, setSub] = useState("jobs");
  const [tick, setTick] = useState(0);
  if (!bizId) return <p className="text-sm text-stone-500">Select a business first.</p>;
  const Tab = { jobs: JobsTab, leads: LeadsTab, calendar: CalendarTab, analytics: AnalyticsTab, revenue: RevenuePanel, adapters: AdaptersTab }[sub];
  return (
    <div className="space-y-4" data-testid="automation-section">
      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="automation-simulation-banner">
        <b>Simulation mode.</b> Every external channel (WhatsApp, Instagram, Facebook, Meta Ads, voice) is disabled. Follow-ups and posts are simulated; nothing leaves Find2near. In-app owner alerts are real.
      </div>
      <Controls bizId={bizId} onRan={() => setTick((t) => t + 1)} />
      <div className="flex gap-1 flex-wrap">
        {SUBTABS.map(([k, l]) => <button key={k} data-testid={`automation-tab-${k}`} onClick={() => setSub(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${sub === k ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>{l}</button>)}
      </div>
      <div className="rounded-2xl bg-white border border-stone-200 p-4"><Tab bizId={bizId} tick={tick} /></div>
    </div>
  );
}
